// Footer colours come from each network's webpage settings, so nothing here may assume a fixed
// palette: any unset colour is derived from the background so text always stays readable.

import {
  bestTextOn, BLACK, contrastRatio, ensureContrast, isDark, luminance, mix, parseColor, toHex, withAlpha,
} from 'src/app/core/utils/color.util';

const DEFAULT_FOOTER_BG = '#1E2328';

// settings is merged over the platform defaults (CommanSubscribeService.defaultSettingsData), so a
// footer colour equal to the default was never chosen by the network and is treated as unset.
function chosen(s: any, defaults: any, key: string): string {
  const norm = (v: any) => String(v || '').replace('!important', '').trim().toLowerCase();
  return s[key] && norm(s[key]) !== norm(defaults[key]) ? s[key] : '';
}

/**
 * CSS custom properties for the footer, derived from the network's webpage settings. Text is
 * neutral (crisp on any background); the network's colour carries the accents - top bar, labels,
 * hovers and a soft glow - so every network reads as its own brand without hurting legibility.
 */
export function buildFooterTheme(settings: any, defaults: any = {}): { [key: string]: string } {
  const s = settings || {};
  const d = defaults || {};
  const page = parseColor(s.main_bg_color);
  const brand = chosen(s, d, 'footer_font_primary_color') || s.bg_primary_color || '';
  const brandRgb = parseColor(brand);
  // An untouched footer takes a deeper, brand-tinted shade of the page so it belongs to the theme.
  let bg = chosen(s, d, 'footer_bg_primary_color');
  if (!bg && page) {
    const tinted = brandRgb ? mix(page, brandRgb, 0.14) : page;
    bg = toHex(mix(tinted, BLACK, luminance(page) < 0.4 ? 0.1 : 0.03));
  }
  bg = bg || DEFAULT_FOOTER_BG;
  const neutral = bestTextOn(bg);
  const bgRgb = parseColor(bg);
  const soft = bgRgb ? toHex(mix(parseColor(neutral), bgRgb, 0.16)) : neutral;
  const accent = brand || neutral;
  return {
    '--ftr-bg': bg,
    '--ftr-heading': neutral,
    '--ftr-text': ensureContrast(soft, bg),
    '--ftr-accent': accent,
    '--ftr-label': ensureContrast(accent, bg),
    '--ftr-glow': brandRgb ? withAlpha(brand, isDark(bg) ? 0.38 : 0.18) : 'transparent',
    '--ftr-surface': withAlpha(neutral, 0.08),
    '--ftr-chip': withAlpha(neutral, 0.16),
    '--ftr-divider': withAlpha(neutral, 0.16),
    // Store badges: solid pills in the opposite tone, like the official store badges.
    '--ftr-badge-bg': neutral,
    '--ftr-badge-text': bestTextOn(neutral),
    '--ftr-logo-panel': isDark(bg) ? 'rgba(255, 255, 255, 0.92)' : 'rgba(17, 17, 17, 0.88)',
  };
}

export interface LogoSample {
  /** Average luminance of the visible pixels; null when none are visible. */
  luminance: number | null;
  /** Share (0-1) of see-through pixels: 0 for a logo drawn on its own solid background. */
  transparentShare: number;
}

/**
 * Average luminance of a logo's visible pixels, or null when it can't be read (e.g. the image
 * host doesn't send CORS headers, which taints the canvas) - callers then skip the contrast panel.
 */
export function sampleLogoLuminance(url: string): Promise<number | null> {
  return sampleLogo(url).then(sample => (sample ? sample.luminance : null));
}

/** A school logo needs the light panel only when it is see-through and its drawing blends into the footer;
 *  a logo on its own solid background (e.g. a purple rectangle) is always visible as it is. */
export function schoolLogoNeedsPanel(sample: LogoSample | null, bg: string): boolean {
  return !!sample && sample.transparentShare >= 0.15 && logoNeedsPanel(sample.luminance, bg);
}

/** Luminance and transparency of a logo, or null when it can't be read (see sampleLogoLuminance). */
export function sampleLogo(url: string): Promise<LogoSample | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onerror = () => resolve(null);
    img.onload = () => {
      try {
        const size = 32;
        const canvas = document.createElement('canvas');
        canvas.width = canvas.height = size;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, size, size);
        const data = ctx.getImageData(0, 0, size, size).data;
        let total = 0;
        let count = 0;
        for (let i = 0; i < data.length; i += 4) {
          if (data[i + 3] > 128) {
            total += luminance([data[i], data[i + 1], data[i + 2]]);
            count++;
          }
        }
        resolve({ luminance: count ? total / count : null, transparentShare: 1 - count / (size * size) });
      } catch (e) {
        resolve(null);
      }
    };
    img.src = url;
  });
}

/** True when the logo would blend into the footer background and needs a contrasting panel. */
export function logoNeedsPanel(logoLuminance: number | null, bg: string): boolean {
  const bgRgb = parseColor(bg);
  if (logoLuminance === null || !bgRgb) {
    return false;
  }
  return contrastRatio(logoLuminance, luminance(bgRgb)) < 1.8;
}
